/**
 * Session Token Store
 *
 * Persists the token in sessionStorage so it survives page refreshes within
 * the same browser tab, but is cleared when the tab is closed.
 *
 * ⚠️  SECURITY NOTE: sessionStorage is readable by any same-origin JavaScript
 * on the page. This store is therefore vulnerable to XSS attacks — a script
 * injection on the same origin can exfiltrate the token. Use InMemoryTokenStore
 * unless tab-persistence is a hard product requirement.
 *
 * Only activate this store via CoreRuntimeConfig.tokenStorageMode =
 * 'memory-session-fallback'. Do NOT use in production environments where XSS
 * risk has not been explicitly accepted and documented.
 */

import { Injectable } from '@angular/core';
import { TokenStore } from './token-store';

const SESSION_TOKEN_KEY = 'aida.auth.token';

@Injectable({providedIn: 'root'})
export class SessionTokenStore extends TokenStore {
  getToken(): string | null {
    try {
      return sessionStorage.getItem(SESSION_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  setToken(token: string): void {
    try {
      sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    } catch {
      // Ignore storage write issues in restricted environments.
    }
  }

  clearToken(): void {
    try {
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
    } catch {
      // Ignore storage clear issues in restricted environments.
    }
  }
}
